import { apiClient } from './client'
import { ApiError } from './errors'

// Midas (Goldie's AI assistant) customer API. Shapes follow the Midas build
// contract; the axios interceptor unwraps the { status, data } envelope.

export type MidasAuthorType = 'customer' | 'midas' | 'agent' | 'system'
export type MidasMessageStatus = 'complete' | 'streaming' | 'error'
export type MidasConversationStatus =
  | 'bot'
  | 'open'
  | 'pending'
  | 'solved'
  | 'closed'

export interface MidasConfig {
  enabled: boolean
  welcome_message: string
  quick_replies: string[]
  avatar_url: string | null
  support_hours?: string | null
}

export interface MidasConversation {
  id: number
  status: MidasConversationStatus
  channel?: string
  subject?: string | null
  handed_over_at: string | null
  last_message_at?: string | null
  created_at?: string
}

export interface MidasMessage {
  id: number
  conversation_id: number
  author_type: MidasAuthorType
  body: string
  status: MidasMessageStatus
  created_at: string
  feedback: 1 | -1 | null
}

export interface MidasMessagesPage {
  messages: MidasMessage[]
  conversation: Pick<MidasConversation, 'id' | 'status' | 'handed_over_at'>
}

export interface MidasSendResult {
  customer_message: MidasMessage
  midas_message: MidasMessage | null
}

async function call<T>(request: () => Promise<{ data: T }>): Promise<T> {
  try {
    const response = await request()
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export function fetchMidasConfig(): Promise<MidasConfig> {
  return call(() => apiClient.get<MidasConfig>('midas/config'))
}

// Creates a conversation, or resumes the customer's open bot conversation.
export async function startMidasConversation(): Promise<MidasConversation> {
  const data = await call(() =>
    apiClient.post<{ conversation: MidasConversation }>('midas/conversations')
  )
  return data.conversation
}

export function fetchMidasMessages(
  conversationId: number,
  afterId: number
): Promise<MidasMessagesPage> {
  return call(() =>
    apiClient.get<MidasMessagesPage>(
      `midas/conversations/${conversationId}/messages`,
      { params: { after_id: afterId } }
    )
  )
}

export function sendMidasMessage(
  conversationId: number,
  body: string
): Promise<MidasSendResult> {
  return call(() =>
    apiClient.post<MidasSendResult>(
      `midas/conversations/${conversationId}/messages`,
      { body }
    )
  )
}

export async function sendMidasFeedback(
  messageId: number,
  value: 1 | -1
): Promise<void> {
  await call(() =>
    apiClient.post(`midas/messages/${messageId}/feedback`, { value })
  )
}
